import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import hexImg from '../images/ImageUpload/hexImg.png';
import colorJSON from './colors.json';
import AdminHeader from '../components/AdminHeader';
import { useMediaQuery } from 'react-responsive';
import '../adminSide/customStyles.css';
import { toast } from 'react-toastify';

function UpdateColors() {
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
    const isMobile = useMediaQuery({ query: '(max-width: 768px)' });
    const navigate = useNavigate();

    const [colors, setColors] = useState([]);
    const [modifiedColors, setModifiedColors] = useState({}); // SAVED colors
    const [loadingColors, setLoadingColors] = useState({});
    const [respectedColorList, setRespectedColorList] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(true); // Loading state for initial fetch
    const [confirmModal, setConfirmModal] = useState({ open: false, type: '', message: '' });

    // Temporary changes (not saved yet)
    const [tempChanges, setTempChanges] = useState({});

    // Track which colors have been modified but not saved
    const unsavedChangesRef = useRef(new Set());

    useEffect(() => {
        const fetchColorSizeData = async () => {
            try {
                const response = await fetch(`${apiBaseUrl}/getAllColorSize/BLACKBATON_2526`);
                const data = await response.json();
                const uniqueColors = [...new Set(data.map(item => item.Color))];
                setColors(uniqueColors);
            } catch (error) {
                console.error('Error fetching color data:', error);
            }
        };

        const fetchModifiedColors = async () => {
            try {
                const response = await fetch(`${apiBaseUrl}/getAllModifiedColors/BLACKBATON_2526`);
                const data = await response.json();
                const modified = data.reduce((acc, item) => {
                    acc[item.db_color] = {
                        name: item.md_color?.toUpperCase() || '',
                        hex: item.hex?.replace('#', '') || ''
                    };
                    return acc;
                }, {});
                setModifiedColors(modified);
            } catch (error) {
                console.error('Error fetching modified colors:', error);
            }
        };

        fetchColorSizeData();
        fetchModifiedColors();

        // Set loading to false after both fetches complete
        Promise.all([fetchColorSizeData(), fetchModifiedColors()]).finally(() => {
            setIsLoading(false);
        });
    }, [apiBaseUrl]);

    // Get the display value for a field (temp change if exists, else saved value)
    const getDisplayValue = (color, field) => {
        // If there's an unsaved change for this color, use it
        if (unsavedChangesRef.current.has(color) && tempChanges[color]?.[field] !== undefined) {
            return tempChanges[color][field];
        }
        // Otherwise use the saved value
        return modifiedColors[color]?.[field] || '';
    };

    // Get the hex value for display in color circle (temp change if exists, else saved value)
    const getHexForDisplay = (color) => {
        if (unsavedChangesRef.current.has(color) && tempChanges[color]?.hex) {
            return tempChanges[color].hex;
        }
        return modifiedColors[color]?.hex || '';
    };

    const handleChangeColor = (e, color) => {
        const val = e.target.value.toUpperCase();

        // Update temp changes
        setTempChanges(prev => ({
            ...prev,
            [color]: {
                ...(prev[color] || {}),
                name: val
            }
        }));

        // Mark as unsaved
        unsavedChangesRef.current.add(color);
    };

    const handleChangeHex = (e, color) => {
        let val = e.target.value.toUpperCase();
        // Remove # if user types it
        if (val.startsWith('#')) {
            val = val.substring(1);
        }
        // Limit to 6 characters for hex code
        if (val.length <= 6) {
            // Update temp changes
            setTempChanges(prev => ({
                ...prev,
                [color]: {
                    ...(prev[color] || {}),
                    hex: val
                }
            }));

            // Mark as unsaved
            unsavedChangesRef.current.add(color);
        }
    };

    const handleGetHexCodes = async (color) => {
        // Toggle dropdown visibility
        if (respectedColorList[color]) {
            setRespectedColorList(prev => {
                const next = { ...prev };
                delete next[color];
                return next;
            });
            return;
        }

        const displayName = getDisplayValue(color, 'name');
        const inputName = displayName?.toLowerCase().trim() || '';

        if (!inputName) {
            toast.info('Please enter a color name first');
            return;
        }

        setLoadingColors(prev => ({ ...prev, [color]: true }));

        try {
            // Try exact match first
            const exactMatches = colorJSON.filter(c =>
                c.name.toLowerCase().trim() === inputName
            );

            // If no exact matches, try partial matches
            let selectedColors = exactMatches.length > 0
                ? exactMatches
                : colorJSON.filter(c =>
                    c.name.toLowerCase().includes(inputName) ||
                    inputName.includes(c.name.toLowerCase().split(' ')[0])
                );

            // If still no matches, try word-by-word matching
            if (selectedColors.length === 0) {
                const inputWords = inputName.split(' ');
                selectedColors = colorJSON.filter(c => {
                    const colorWords = c.name.toLowerCase().split(' ');
                    return inputWords.some(word =>
                        colorWords.some(colorWord =>
                            colorWord.includes(word) || word.includes(colorWord)
                        )
                    );
                });
            }

            if (selectedColors.length > 0) {
                // Sort by relevance
                selectedColors.sort((a, b) => {
                    const aLower = a.name.toLowerCase();
                    const bLower = b.name.toLowerCase();

                    if (aLower === inputName) return -1;
                    if (bLower === inputName) return 1;

                    const aDiff = Math.abs(aLower.length - inputName.length);
                    const bDiff = Math.abs(bLower.length - inputName.length);
                    return aDiff - bDiff;
                });

                setRespectedColorList(prev => ({
                    ...prev,
                    [color]: selectedColors.slice(0, 10)
                }));
            } else {
                toast.info(`No matching colors found for "${inputName}"`);
            }
        } catch (error) {
            console.error('Error finding hex codes:', error);
            toast.info('An error occurred while searching for colors');
        } finally {
            setLoadingColors(prev => ({ ...prev, [color]: false }));
        }
    };

    const handleSelectColor = (color, selectedColor) => {
        // Update temp changes
        setTempChanges(prev => ({
            ...prev,
            [color]: {
                ...(prev[color] || {}),
                name: selectedColor.name.toUpperCase(),
                hex: selectedColor.hex.replace('#', '')
            }
        }));

        // Mark as unsaved
        unsavedChangesRef.current.add(color);

        // Close dropdown
        setRespectedColorList(prev => {
            const next = { ...prev };
            delete next[color];
            return next;
        });
    };

    const handleSave = () => {
        // Check if there are any unsaved changes
        if (unsavedChangesRef.current.size === 0) {
            toast.info('No changes to save');
            return;
        }

        // // Check if all modified colors have both name and hex
        // const invalidColors = [];
        const unsavedColors = Array.from(unsavedChangesRef.current);

        // for (const color of unsavedColors) {
        //     const tempData = tempChanges[color];
        //     // if (!tempData?.name?.trim() || !tempData?.hex?.trim()) {
        //     //     invalidColors.push(color);
        //     // }
        //     if (!tempData?.hex?.trim()) {
        //         invalidColors.push(color);
        //     }
        // }

        // if (invalidColors.length > 0) {
        //     toast.info(`Please fill both Display Name and Hex Code for: ${invalidColors.join(', ')}`);
        //     return;
        // }

        // Confirm save
        setConfirmModal({
            open: true,
            type: 'save',
            message: 'Are you sure you want to save all color changes?'
        });
    };

    const executeSave = async () => {
        const unsavedColors = Array.from(unsavedChangesRef.current);

        setIsSaving(true);
        try {
            // Create new saved colors by merging temp changes
            const newModifiedColors = { ...modifiedColors };

            unsavedColors.forEach(color => {
                newModifiedColors[color] = {
                    name: tempChanges[color]?.name || '',
                    hex: tempChanges[color]?.hex || ''
                };
            });

            const saveData = {};

            unsavedColors.forEach(color => {
                saveData[color] = {
                    name: tempChanges[color]?.name || modifiedColors[color]?.name || '',
                    hex: tempChanges[color]?.hex
                        ? `#${tempChanges[color].hex.replace('#', '')}`
                        : modifiedColors[color]?.hex || ''
                };
            });

            // console.log(saveData, 'saveData');


            const response = await axios.post(
                `${apiBaseUrl}/postModifyColor/BLACKBATON_2526`,
                saveData,
                {
                    headers: {
                        "Content-Type": "application/json"
                    }
                }
            );


            if (response.status === 200) {
                toast.info('Colors saved successfully!');
                // Update saved colors
                setModifiedColors(newModifiedColors);
                // Clear temp changes
                setTempChanges({});
                unsavedChangesRef.current.clear();
                navigate('/dashboard');
            }
        } catch (error) {
            console.error('Error saving colors:', error);
            toast.info('Failed to save colors. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        if (unsavedChangesRef.current.size > 0) {
            setConfirmModal({
                open: true,
                type: 'cancel',
                message: 'You have unsaved changes. Are you sure you want to cancel?'
            });
        } else {
            navigate('/dashboard');
        }
    };

    const handleConfirmProceed = async () => {
        const { type } = confirmModal;
        setConfirmModal({ open: false, type: '', message: '' });
        if (type === 'save') {
            await executeSave();
        } else if (type === 'cancel') {
            navigate('/dashboard');
        }
    };

    // Helper function to determine if a color has a hex code (for categorization)
    const hasHexCode = (color) => {
        // ONLY check saved colors for categorization
        const savedHex = modifiedColors[color]?.hex;
        return savedHex && savedHex.trim() !== '';
    };

    // Separate colors into categories based on SAVED data only
    const emptyHexColors = colors.filter(color => !hasHexCode(color));
    const filledHexColors = colors.filter(color => hasHexCode(color));

    // Filter colors based on search term
    const filterColorsBySearch = (colorArray) => {
        if (!searchTerm.trim()) return colorArray;

        const searchLower = searchTerm.toLowerCase();
        return colorArray.filter(color => {
            const systemName = color?.toLowerCase() || '';
            const displayName = getDisplayValue(color, 'name')?.toLowerCase() || '';
            return systemName.includes(searchLower) || displayName.includes(searchLower);
        });
    };

    const filteredEmptyHexColors = filterColorsBySearch(emptyHexColors);
    const filteredFilledHexColors = filterColorsBySearch(filledHexColors);

    // Sort function
    const safeSort = (a, b) => {
        if (!a && !b) return 0;
        if (!a) return 1;
        if (!b) return -1;
        return String(a).localeCompare(String(b));
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (Object.keys(respectedColorList).length > 0) {
                if (!e.target.closest('.color-dropdown-container')) {
                    setRespectedColorList({});
                }
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [respectedColorList]);

    return (
        <>
        <div className="min-h-screen bg-gray-50 flex flex-col font-dmSans">
            <AdminHeader />

            <div className="flex-grow py-12 lg:px-12 px-4 shadow-inner">
                <div className="w-full max-w-full mx-auto">
                    <div className="sticky top-20 z-30 bg-black py-8 px-8 rounded-t-2xl shadow-xl flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                        <div className="flex-1 text-left">
                            <h1 className="text-2xl font-extrabold text-white font-montserrat tracking-tight">
                                MANAGE COLORS
                            </h1>
                            <p className="text-gray-400 mt-2 text-sm">
                                Total: {colors.length} |
                                Missing: {emptyHexColors.length} |
                                Completed: {filledHexColors.length} |
                                Unsaved: {unsavedChangesRef.current.size}
                            </p>
                        </div>

                        <div className="flex flex-col md:flex-row gap-4 w-full md:w-auto">
                            {/* Search input */}
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder="Search by name..."
                                    className="w-full md:w-64 px-4 py-2 bg-gray-800 border border-gray-600 text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent text-sm"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                {searchTerm && (
                                    <button
                                        onClick={() => setSearchTerm('')}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                                    >
                                        ?
                                    </button>
                                )}
                            </div>

                            <div className="flex gap-4">
                                <button
                                    onClick={handleCancel}
                                    className="px-6 py-2 bg-transparent border border-gray-600 text-gray-300 rounded-xl hover:bg-gray-800 hover:text-white transition-all text-sm font-bold uppercase tracking-widest whitespace-nowrap"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSave}
                                    disabled={isSaving || unsavedChangesRef.current.size === 0}
                                    className={`px-8 py-2 rounded-xl text-sm font-bold uppercase tracking-widest transition-all shadow-lg whitespace-nowrap
                                        ${isSaving ? 'bg-gray-600 cursor-not-allowed' :
                                            unsavedChangesRef.current.size === 0 ? 'bg-gray-300 text-gray-500 cursor-not-allowed' :
                                                'bg-white text-black hover:bg-gray-100 active:scale-[0.98]'}`}
                                >
                                    {isSaving ? 'Saving...' : 'Save All'}
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white shadow-2xl rounded-b-2xl border-x border-b border-gray-100 p-8">
                        {/* Search results info */}
                        {searchTerm && (
                            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-xl">
                                <p className="text-sm text-blue-700">
                                    Found {filteredEmptyHexColors.length + filteredFilledHexColors.length} results for "{searchTerm}"
                                    <button
                                        onClick={() => setSearchTerm('')}
                                        className="ml-2 text-blue-500 hover:text-blue-700 underline"
                                    >
                                        Clear
                                    </button>
                                </p>
                            </div>
                        )}

                        {isLoading ? (
                            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6 animate-pulse">
                                {[...Array(8)].map((_, i) => (
                                    <div key={i} className="p-6 bg-gray-50 rounded-2xl border border-gray-200 h-64 flex flex-col justify-between">
                                        <div className="flex justify-between items-start">
                                            <div className="w-24 h-4 bg-gray-200 rounded"></div>
                                            <div className="w-10 h-10 rounded-full bg-gray-300"></div>
                                        </div>
                                        <div className="space-y-4">
                                            <div className="h-10 bg-gray-200 rounded-xl w-full"></div>
                                            <div className="h-10 bg-gray-200 rounded-xl w-full"></div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <>
                                {/* Section for empty hex codes (BASED ON SAVED DATA) */}
                                {filteredEmptyHexColors.length > 0 && (
                                    <div className="mb-10">
                                        {!searchTerm && (
                                            <div className="flex items-center gap-3 mb-6">
                                                <div className="w-4 h-4 rounded-full bg-red-100 border-2 border-red-400" />
                                                <h2 className="text-xl font-bold text-gray-800 font-montserrat">
                                                    Missing Hex Codes ({emptyHexColors.length})
                                                </h2>
                                                <div className="flex-1 h-px bg-gradient-to-r from-red-200/50 to-transparent" />
                                            </div>
                                        )}
                                        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
                                            {filteredEmptyHexColors
                                                .sort(safeSort)
                                                .map((color, idx) => {
                                                    const displayName = getDisplayValue(color, 'name');
                                                    const displayHex = getDisplayValue(color, 'hex');
                                                    const hasUnsavedChanges = unsavedChangesRef.current.has(color);

                                                    return (
                                                        <div key={`empty-${idx}`} className="color-dropdown-container group p-6 bg-red-50 rounded-2xl border-2 border-red-100 hover:border-red-200 hover:shadow-xl transition-all duration-300 space-y-4 relative">
                                                            {hasUnsavedChanges && (
                                                                <div className="absolute top-3 right-3 bg-yellow-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                                                                    UNSAVED
                                                                </div>
                                                            )}
                                                            <div className="absolute top-0 right-0 w-16 h-16 bg-red-100 rounded-full -translate-y-8 translate-x-8 opacity-50" />

                                                            <div className="flex justify-between items-start relative z-10">
                                                                <div className="space-y-1 text-left">
                                                                    <span className="text-[10px] font-bold text-red-600 tracking-widest uppercase">
                                                                        System Name
                                                                    </span>
                                                                    <h3 className="text-lg font-bold text-gray-900 font-montserrat truncate">
                                                                        {color || 'Unnamed Color'}
                                                                    </h3>
                                                                </div>
                                                                <div
                                                                    className="w-10 h-10 rounded-full border-2 border-red-200 shadow-inner overflow-hidden flex-shrink-0 relative"
                                                                    style={{
                                                                        backgroundColor: displayHex ? `#${displayHex}` : '#fca5a5'
                                                                    }}
                                                                >
                                                                    {!displayHex && (
                                                                        <div className="absolute inset-0 flex items-center justify-center">
                                                                            <div className="text-xs font-bold text-red-600">?</div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            <div className="space-y-4 relative z-10">
                                                                <div className="space-y-2">
                                                                    <label className="text-[10px] font-bold text-red-600 tracking-widest uppercase ml-1">
                                                                        Display Name
                                                                    </label>
                                                                    <div className="relative">
                                                                        <input
                                                                            type="text"
                                                                            placeholder="Enter color name..."
                                                                            className="w-full px-4 py-2 bg-white border border-red-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 transition-all font-montserrat font-semibold text-sm"
                                                                            value={displayName}
                                                                            onChange={(e) => handleChangeColor(e, color)}
                                                                            onClick={(e) => e.stopPropagation()}
                                                                        />
                                                                        {displayName && (
                                                                            <button
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    handleGetHexCodes(color);
                                                                                }}
                                                                                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                                                                            >
                                                                                <img className='w-4 h-4' src={hexImg} alt="Get Hex" />
                                                                            </button>
                                                                        )}

                                                                        {respectedColorList[color] && (
                                                                            <div className="absolute top-full left-0 w-full mt-2 bg-white shadow-2xl rounded-xl border border-gray-200 z-50 max-h-48 overflow-y-auto custom-scroll p-2">
                                                                                {respectedColorList[color].map((opt, i) => (
                                                                                    <div
                                                                                        key={i}
                                                                                        className="flex items-center gap-3 p-2 hover:bg-red-50 rounded-lg cursor-pointer transition-colors mb-1 last:mb-0"
                                                                                        onClick={() => handleSelectColor(color, opt)}
                                                                                    >
                                                                                        <div
                                                                                            className="w-6 h-6 rounded-full border border-gray-300 shadow-sm flex-shrink-0"
                                                                                            style={{ backgroundColor: opt.hex }}
                                                                                        />
                                                                                        <div className="flex-1 min-w-0">
                                                                                            <div className="text-sm font-semibold text-gray-800 truncate">
                                                                                                {opt.name}
                                                                                            </div>
                                                                                            <div className="text-xs text-gray-600 font-mono uppercase">
                                                                                                {opt.hex}
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>

                                                                <div className="space-y-2">
                                                                    <label className="text-[10px] font-bold text-red-600 tracking-widest uppercase ml-1">
                                                                        Hex Code
                                                                    </label>
                                                                    <div className="flex items-center gap-3">
                                                                        <div className="relative flex-1">
                                                                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-red-400 font-bold text-sm">#</span>
                                                                            <input
                                                                                type="text"
                                                                                placeholder="FFFFFF"
                                                                                className="w-full pl-10 pr-4 py-2 bg-white border border-red-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 transition-all font-mono font-semibold text-sm uppercase"
                                                                                value={displayHex}
                                                                                onChange={(e) => handleChangeHex(e, color)}
                                                                                onClick={(e) => e.stopPropagation()}
                                                                                maxLength={6}
                                                                            />
                                                                        </div>
                                                                        {loadingColors[color] && (
                                                                            <div className="w-4 h-4 border-2 border-red-200 border-t-red-500 rounded-full animate-spin" />
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                        </div>
                                    </div>
                                )}

                                {/* Section for colors with hex codes (BASED ON SAVED DATA) */}
                                {filteredFilledHexColors.length > 0 && (
                                    <div>
                                        {!searchTerm && (
                                            <div className="flex items-center gap-3 mb-6">
                                                <div className="w-4 h-4 rounded-full bg-green-100 border-2 border-green-400" />
                                                <h2 className="text-xl font-bold text-gray-800 font-montserrat">
                                                    Colors with Hex Codes ({filledHexColors.length})
                                                </h2>
                                                <div className="flex-1 h-px bg-gradient-to-r from-green-200/50 to-transparent" />
                                            </div>
                                        )}
                                        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
                                            {filteredFilledHexColors
                                                .sort(safeSort)
                                                .map((color, idx) => {
                                                    const displayName = getDisplayValue(color, 'name');
                                                    const displayHex = getDisplayValue(color, 'hex');
                                                    const hasUnsavedChanges = unsavedChangesRef.current.has(color);
                                                    const savedHex = modifiedColors[color]?.hex || '';

                                                    // NEW: Check if display name is empty (for saved colors)
                                                    const savedDisplayName = modifiedColors[color]?.name || '';
                                                    const isMissingDisplayName = savedDisplayName.trim() === '';

                                                    // Determine background color based on missing display name
                                                    const backgroundColorClass = isMissingDisplayName ? 'bg-red-50' : 'bg-green-50';
                                                    const borderColorClass = isMissingDisplayName ? 'border-red-100 hover:border-red-200' : 'border-green-100 hover:border-green-200';
                                                    const accentColorClass = isMissingDisplayName ? 'text-red-600' : 'text-green-600';
                                                    const borderAccentColorClass = isMissingDisplayName ? 'border-red-200' : 'border-green-200';
                                                    const focusRingColorClass = isMissingDisplayName ? 'focus:ring-red-500' : 'focus:ring-green-500';
                                                    const hashColorClass = isMissingDisplayName ? 'text-red-400' : 'text-green-400';
                                                    const dotColorClass = isMissingDisplayName ? 'bg-red-100 border-red-400' : 'bg-green-100 border-green-400';

                                                    return (
                                                        <div
                                                            key={`filled-${idx}`}
                                                            className={`color-dropdown-container group p-6 rounded-2xl border ${backgroundColorClass} ${borderColorClass} hover:shadow-xl transition-all duration-300 space-y-4`}
                                                        >
                                                            {/* Missing Display Name Warning */}
                                                            {isMissingDisplayName && (
                                                                <div className="absolute top-3 left-3 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                                                                    NO NAME
                                                                </div>
                                                            )}

                                                            {hasUnsavedChanges && (
                                                                <div className="absolute top-3 right-3 bg-yellow-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                                                                    UNSAVED
                                                                </div>
                                                            )}

                                                            <div className="flex justify-between items-start">
                                                                <div className="space-y-1">
                                                                    <span className={`text-[10px] font-bold tracking-widest uppercase ${accentColorClass}`}>
                                                                        System Name
                                                                    </span>
                                                                    <h3 className="text-lg font-bold text-gray-900 font-montserrat truncate">
                                                                        {color || 'Unnamed Color'}
                                                                    </h3>
                                                                </div>
                                                                <div
                                                                    className="w-10 h-10 rounded-full border shadow-inner overflow-hidden flex-shrink-0"
                                                                    style={{
                                                                        backgroundColor: displayHex ? `#${displayHex}` : `#${savedHex}`,
                                                                        borderColor: isMissingDisplayName ? '#fecaca' : '#bbf7d0'
                                                                    }}
                                                                />
                                                            </div>

                                                            <div className="space-y-4">
                                                                <div className="space-y-2">
                                                                    <label className={`text-[10px] font-bold tracking-widest uppercase ml-1 ${accentColorClass}`}>
                                                                        Display Name
                                                                    </label>
                                                                    <div className="relative">
                                                                        <input
                                                                            type="text"
                                                                            placeholder="Enter name..."
                                                                            className={`w-full px-4 py-2 bg-white border rounded-xl focus:outline-none focus:ring-2 transition-all font-montserrat font-semibold text-sm ${borderAccentColorClass} ${focusRingColorClass}`}
                                                                            value={displayName}
                                                                            onChange={(e) => handleChangeColor(e, color)}
                                                                            onClick={(e) => e.stopPropagation()}
                                                                        />
                                                                        {displayName && (
                                                                            <button
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    handleGetHexCodes(color);
                                                                                }}
                                                                                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 hover:bg-gray-50 rounded-lg transition-colors"
                                                                            >
                                                                                <img className='w-4 h-4' src={hexImg} alt="Get Hex" />
                                                                            </button>
                                                                        )}

                                                                        {respectedColorList[color] && (
                                                                            <div className="absolute top-full left-0 w-full mt-2 bg-white shadow-2xl rounded-xl border border-gray-200 z-50 max-h-48 overflow-y-auto custom-scroll p-2">
                                                                                {respectedColorList[color].map((opt, i) => (
                                                                                    <div
                                                                                        key={i}
                                                                                        className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors mb-1 last:mb-0"
                                                                                        onClick={() => handleSelectColor(color, opt)}
                                                                                    >
                                                                                        <div
                                                                                            className="w-6 h-6 rounded-full border border-gray-300 shadow-sm flex-shrink-0"
                                                                                            style={{ backgroundColor: opt.hex }}
                                                                                        />
                                                                                        <div className="flex-1 min-w-0">
                                                                                            <div className="text-sm font-semibold text-gray-800 truncate">
                                                                                                {opt.name}
                                                                                            </div>
                                                                                            <div className="text-xs text-gray-600 font-mono uppercase">
                                                                                                {opt.hex}
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>

                                                                <div className="space-y-2">
                                                                    <label className={`text-[10px] font-bold tracking-widest uppercase ml-1 ${accentColorClass}`}>
                                                                        Hex Code
                                                                    </label>
                                                                    <div className="flex items-center gap-3">
                                                                        <div className="relative flex-1">
                                                                            <span className={`absolute left-4 top-1/2 -translate-y-1/2 font-bold text-sm ${hashColorClass}`}>#</span>
                                                                            <input
                                                                                type="text"
                                                                                placeholder="FFFFFF"
                                                                                className={`w-full pl-10 pr-4 py-2 bg-white border rounded-xl focus:outline-none focus:ring-2 transition-all font-mono font-semibold text-sm uppercase ${borderAccentColorClass} ${focusRingColorClass}`}
                                                                                value={displayHex}
                                                                                onChange={(e) => handleChangeHex(e, color)}
                                                                                onClick={(e) => e.stopPropagation()}
                                                                                maxLength={6}
                                                                            />
                                                                        </div>
                                                                        {loadingColors[color] && (
                                                                            <div className="w-4 h-4 border-2 rounded-full animate-spin"
                                                                                style={{
                                                                                    borderColor: isMissingDisplayName ? '#fecaca' : '#bbf7d0',
                                                                                    borderTopColor: isMissingDisplayName ? '#ef4444' : '#22c55e'
                                                                                }}
                                                                            />
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
        {confirmModal.open && (
            <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4">
                <div className="w-full max-w-md bg-white rounded-2xl p-6 shadow-2xl text-left">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Please Confirm</h3>
                    <p className="text-sm text-gray-600 mb-6">{confirmModal.message}</p>
                    <div className="flex justify-end gap-3">
                        <button
                            type="button"
                            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700"
                            onClick={() => setConfirmModal({ open: false, type: '', message: '' })}
                        >
                            No
                        </button>
                        <button
                            type="button"
                            className="px-4 py-2 rounded-lg bg-black text-white"
                            onClick={handleConfirmProceed}
                        >
                            Yes
                        </button>
                    </div>
                </div>
            </div>
        )}
        </>
    );
}

export default UpdateColors;




